import { useState, useCallback, useRef } from 'react';

import { GetPageApi } from 'js/social/api/feed_api';
import {
  NewsType,
  Comments,
  Comment,
  News,
  FollowableUser,
  Post,
} from 'js/social/shared/types';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';

import { FeedPage, FeedConfigInNewsFeedResponse } from './useFeedConfig';
import { currentUser } from 'js/utils/user';

const transformCommentsInNews =
  (
    type: NewsType,
    commentableId: number | string,
    transform: (comments: Comments) => Comments,
  ) =>
  (news: News[]): News[] =>
    news.map((n) => {
      // Early terminate if commentable is unaffected
      if (n.newsType !== type || n.newsId !== commentableId) {
        return n;
      }

      return {
        ...n,
        comments: transform(n.comments),
      };
    });

const transformRepliesInNews = (
  type: NewsType,
  commentableId: number | string,
  parentCommentId: number | string,
  transform: (comments: Comments) => Comments,
  transformParent: (
    parent: Omit<Comments, 'data'>,
  ) => Omit<Comments, 'data'> = (c) => c,
) =>
  transformCommentsInNews(type, commentableId, (comments) => {
    // HACK: run all transforms first before transforming parent
    // This is mainly so that we can collect how many total comments
    // are removed before updating the count of the parent.
    const newData = comments.data.map((comment) => {
      // Early terminate if top level comment is unaffected
      if (comment.id !== parentCommentId) {
        return comment;
      }

      return {
        ...comment,
        replies: transform(comment.replies as Comments),
      };
    });
    return {
      ...transformParent(comments),
      data: newData,
    };
  });

const logHomeData = (
  page: string,
  currPagePosts: Array<News>,
  pageNumber: number,
  openalexRecsTestBucket?: string,
) => {
  if (page === 'home') {
    // Only track home page content mix for now
    const counts = currPagePosts.reduce(
      (tally, news) => {
        tally[news.newsType] = (tally[news.newsType] || 0) + 1;
        return tally;
      },
      {} as Record<NewsType, number>,
    );

    // Don't log content mix if we're showing them fallback content for empty feed
    if (
      currPagePosts.length > 0 &&
      currPagePosts[0].reason.type !== 'fallback_content'
    ) {
      arbitraryEvents.write('SocialFeedContentMix', {
        page,
        page_number: pageNumber,
        posts_count: counts['post'] || 0,
        paper_updates_count: counts['paper_update'] || 0,
      });
    }
    currPagePosts.forEach((news, idx) => {
      // (SOC-1355) Log the type and feed reason of each news item being served
      arbitraryEvents.write('SocialFeedItemComposition', {
        page_idx: pageNumber,
        news_idx: idx + 1, // 1-indexed to match how the page_idx works, for consistency
        news_type: news.newsType,
        feed_reason: news.reason.type,
      });
      if (
        news.reason.type === 'recommended' &&
        !openalexRecsTestBucket // skip logging if in openalex recs test -- these recs don't have a score
      ) {
        arbitraryEvents.write('RecommendedContentServed', {
          page_number: pageNumber,
          user_id: currentUser!.id,
          content_type: news.newsType,
          content_id: `${news.newsId}`,
          recommendation_score: news.recommendationScore,
        });
      }
    });
  }
};

const makeDeferred = <T>() => {
  let resolve = (_: T | PromiseLike<T>) => {};
  let reject = () => {};
  const promise = new Promise<T>((_resolve, _reject) => {
    resolve = _resolve;
    reject = _reject;
  });
  return {
    promise,
    resolve,
    reject,
  };
};

const getPassthroughParams = () => {
  const allowedPassthroughKeys = ['interview'];
  const params = new URLSearchParams(window.location.search);
  const passthroughParams: Record<string, string> = {};
  for (const key of allowedPassthroughKeys) {
    if (params.has(key)) {
      passthroughParams[key] = params.get(key) || '';
    }
  }
  return passthroughParams;
};

// Used to manage state for posts
const useNews = ({
  page,
  getPage,
  setMoreUsers,
  setFeedConfig,
  hoistedContentId,
  hoistedContentType,
  hoistedContentPost,
  freeformTopicId,
  isFirstVisit,
}: {
  page: FeedPage;
  getPage: GetPageApi;
  setMoreUsers: (users: FollowableUser[]) => void;
  setFeedConfig: (config: FeedConfigInNewsFeedResponse) => void;
  hoistedContentId?: string;
  hoistedContentType?: string;
  hoistedContentPost?: News;
  freeformTopicId?: string;
  isFirstVisit?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedNews, setHasLoadedNews] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [noNewsFound, setNoNewsFound] = useState(false);
  const [news, setNews] = useState<News[]>(
    hoistedContentPost ? [hoistedContentPost] : [],
  );
  const pageNumberRef = useRef(0);

  const hasNextPage = useRef(true);
  const nextPageParams = useRef<object | null>(null);

  const newsFeedLoadedRef = useRef(Promise.resolve(true));

  const newsString = news
    .map((thisNews) => `${thisNews.newsType}-${thisNews.newsId}`)
    .join(';');
  const encodedNewsString =
    typeof window !== 'undefined' ? btoa(newsString) : '';

  const fetchNextPage = useCallback(() => {
    if (isLoading) {
      return null;
    }

    const currentPage = pageNumberRef.current;
    setIsLoading(true);
    const newsFeedLoadDeferred = makeDeferred<boolean>();
    newsFeedLoadedRef.current = newsFeedLoadDeferred.promise;

    getPage({
      ...(hoistedContentId && {
        hoisted_content_id: hoistedContentId,
        hoisted_content_type: hoistedContentType,
      }),
      ...(freeformTopicId && {
        freeform_topic_id: freeformTopicId,
      }),
      seen_news: encodedNewsString,
      page_number: pageNumberRef.current,
      hit_id: window.Aedu?.hit_data?.id,
      ...nextPageParams.current,
      ...getPassthroughParams(),
      ...(isFirstVisit && {
        is_first_visit: '1',
      }),
    })
      .then((res) => {
        // Sometimes multiple requests are made to the backend, resulting in duplicate feed pages.
        // To prevent this, only update the newsfeed if the content is not "stale". (SOC-1196)
        if (currentPage === pageNumberRef.current) {
          // Make user data available first before updating posts
          setMoreUsers(res.users);
          setNews((currentNews) => [...currentNews, ...res.posts]);
          setFeedConfig(res);
          nextPageParams.current = res.meta.next;
          hasNextPage.current = res.meta.next != null;
          pageNumberRef.current += 1;
          logHomeData(
            page,
            res.posts,
            pageNumberRef.current,
            res.openalexRecsTestBucket,
          );
          setNoNewsFound(res.posts.length === 0);
          setHasLoadedNews(true);
        }
      })
      .catch((e) => {
        setHasError(true);
        console.error(e);
      })
      .finally(() => {
        setIsLoading(false);
        newsFeedLoadDeferred.resolve(true);
      });
  }, [
    getPage,
    page,
    setMoreUsers,
    setFeedConfig,
    isLoading,
    hoistedContentId,
    hoistedContentType,
    encodedNewsString,
    freeformTopicId,
    isFirstVisit,
  ]);

  const refreshFeed = useCallback(async () => {
    if (isLoading) {
      return null;
    }

    pageNumberRef.current = 0;
    hasNextPage.current = true;
    setHasLoadedNews(false);

    setIsLoading(true);
    setNews(hoistedContentPost ? [hoistedContentPost] : []);
    const newsFeedLoadDeferred = makeDeferred<boolean>();
    newsFeedLoadedRef.current = newsFeedLoadDeferred.promise;
    try {
      const res = await getPage({
        ...(hoistedContentId && {
          hoisted_content_id: hoistedContentId,
          hoisted_content_type: hoistedContentType,
        }),
        ...(freeformTopicId && {
          freeform_topic_id: freeformTopicId,
        }),
        seen_news: '',
        page_number: 0,
        hit_id: window.Aedu?.hit_data?.id,
        ...getPassthroughParams(),
        ...(isFirstVisit && {
          is_first_visit: '1',
        }),
      });
      setMoreUsers(res.users);
      setNews(
        hoistedContentPost ? [hoistedContentPost, ...res.posts] : res.posts,
      );
      setFeedConfig(res);
      setHasLoadedNews(true);
      nextPageParams.current = res.meta.next;
      hasNextPage.current = res.meta.next != null;
      pageNumberRef.current += 1;
      logHomeData(
        page,
        res.posts,
        pageNumberRef.current,
        res.openalexRecsTestBucket,
      );
    } catch (e) {
      setHasError(true);
      console.error(e);
    } finally {
      setIsLoading(false);
      newsFeedLoadDeferred.resolve(true);
    }
  }, [
    isLoading,
    getPage,
    hoistedContentId,
    hoistedContentType,
    hoistedContentPost,
    setMoreUsers,
    setFeedConfig,
    page,
    freeformTopicId,
    isFirstVisit,
  ]);

  const removeUserContent = useCallback((userId: number) => {
    setNews((news) => {
      return news
        .filter((news) => news.userId !== userId)
        .filter((news) => {
          const sourceUserIds = news.reason.data?.sourceUserIds || [];
          const attributedToUser =
            sourceUserIds.length === 1 && sourceUserIds[0] === userId;
          return !attributedToUser;
        })
        .filter(
          (news) =>
            !(
              news.newsType === 'post' &&
              news.quotable &&
              news.quotable.userId === userId
            ),
        )
        .map((news) => {
          news.comments.data = news.comments.data
            .filter((comment) => comment.userId !== userId)
            .map((comment) => {
              comment.replies.data = comment.replies.data.filter(
                (reply) => reply.userId !== userId,
              );
              return comment;
            });
          return news;
        });
    });
  }, []);

  const addFeedNews = useCallback(
    (post: Post, users: FollowableUser[]) => {
      setMoreUsers(users);
      setNews((news) => [post, ...news]);
    },
    [setMoreUsers],
  );

  const removeFeedNews = useCallback(
    (id: number | string, type: NewsType) =>
      setNews((news) =>
        news.filter((n) => n.newsType !== type || n.newsId !== id),
      ),
    [],
  );

  const updateFeedNews = useCallback(
    (id: number | string, type: NewsType, update: News) => {
      setNews((news) => {
        return news.map((n) => {
          if (n.newsType === type && n.newsId === id) {
            return update;
          }
          return n;
        });
      });
    },
    [],
  );

  const likeFeedNews = useCallback(
    (id: number | string, type: NewsType) =>
      setNews((news) =>
        news.map((n) => {
          if (n.newsType === type && n.newsId === id) {
            if (!n.reactions.like.reacted) {
              n.reactions.like.reacted = true;
              n.reactions.like.count += 1;
            }
          }
          return n;
        }),
      ),
    [],
  );

  const unlikeFeedNews = useCallback(
    (id: number | string, type: NewsType) =>
      setNews((news) =>
        news.map((n) => {
          if (n.newsType === type && n.newsId === id) {
            n.reactions.like.reacted = false;
            n.reactions.like.count = Math.max(0, n.reactions.like.count - 1);
          }
          return n;
        }),
      ),
    [],
  );

  const repostFeedNews = useCallback(
    (id: number | string, type: NewsType) =>
      setNews((news) =>
        news.map((n) => {
          if (n.newsType === type && n.newsId === id) {
            if (!n.reactions.repost.reacted) {
              n.reactions.repost.reacted = true;
              n.reactions.repost.count += 1;
            }
          }
          return n;
        }),
      ),
    [],
  );

  const unrepostFeedNews = useCallback(
    (id: number | string, type: NewsType) =>
      setNews((news) =>
        news.map((n) => {
          if (n.newsType === type && n.newsId === id) {
            n.reactions.repost.reacted = false;
            n.reactions.repost.count = Math.max(
              0,
              n.reactions.repost.count - 1,
            );
          }
          return n;
        }),
      ),
    [],
  );

  const updateWorkBookmarkStatus = useCallback(
    (workId: number, bookmarked: boolean) => {
      setNews((news) =>
        news.map((n) => {
          if (n.work?.id === workId) {
            n.work.bookmarkedByCurrentUser = bookmarked;
            if (bookmarked) {
              n.work.bookmarkCount += 1;
            } else {
              n.work.bookmarkCount = Math.max(n.work.bookmarkCount - 1, 0);
            }
          }
          return n;
        }),
      );
    },
    [],
  );

  const addFeedComment = useCallback(
    (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      comment: Comment,
      users: FollowableUser[],
    ) => {
      setMoreUsers(users);

      const transformFn = (comments: Comments): Comments => ({
        ...comments,
        data: [...comments.data, comment],
        count: comments.count + 1,
      });
      if (parentCommentId == null) {
        setNews(transformCommentsInNews(type, commentableId, transformFn));
      } else {
        setNews(
          transformRepliesInNews(
            type,
            commentableId,
            parentCommentId,
            transformFn,
            (parent) => ({
              ...parent,
              count: parent.count + 1,
            }),
          ),
        );
      }
    },
    [setMoreUsers],
  );

  const removeFeedComment = useCallback(
    (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      id: number | string,
    ) => {
      let totalDeletedCount = 0;

      const transformFn = (comments: Comments) => {
        let deletedReplyCount = 0;
        const data = comments.data.filter((comment) => {
          if (comment.id !== id) {
            return true;
          } else {
            deletedReplyCount += comment.replies?.count || 0;
            return false;
          }
        });

        const deletedCount = 1 + deletedReplyCount;
        totalDeletedCount += deletedCount;

        return {
          ...comments,
          data,
          count: Math.max(comments.count - deletedCount, 0),
        };
      };

      if (parentCommentId == null) {
        setNews(transformCommentsInNews(type, commentableId, transformFn));
      } else {
        setNews(
          transformRepliesInNews(
            type,
            commentableId,
            parentCommentId,
            transformFn,
            (parent) => ({
              ...parent,
              count: Math.max(parent.count - totalDeletedCount, 0),
            }),
          ),
        );
      }
    },
    [],
  );

  const updateFeedComment = useCallback(
    (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      id: number | string,
      update: Comment,
    ) => {
      const transform = (comments: Comments) => ({
        ...comments,
        data: comments.data.map((comment) => {
          if (comment.id === id) {
            return update;
          }
          return comment;
        }),
      });

      if (!parentCommentId) {
        setNews(transformCommentsInNews(type, commentableId, transform));
      } else {
        setNews(
          transformRepliesInNews(
            type,
            commentableId,
            parentCommentId,
            transform,
          ),
        );
      }
    },
    [],
  );

  const likeFeedComment = useCallback(
    (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      id: number | string,
    ) => {
      const transformFn = (comments: Comments) => ({
        ...comments,
        data: comments.data.map((comment) => {
          if (comment.id === id) {
            comment.reactions.like.reacted = true;
            comment.reactions.like.count += 1;
          }
          return comment;
        }),
      });

      if (parentCommentId == null) {
        setNews(transformCommentsInNews(type, commentableId, transformFn));
      } else {
        setNews(
          transformRepliesInNews(
            type,
            commentableId,
            parentCommentId,
            transformFn,
          ),
        );
      }
    },
    [],
  );

  const unlikeFeedComment = useCallback(
    (
      type: NewsType,
      commentableId: number | string,
      parentCommentId: number | string | null,
      id: number | string,
    ) => {
      const transformFn = (comments: Comments) => ({
        ...comments,
        data: comments.data.map((comment) => {
          if (comment.id === id) {
            comment.reactions.like.reacted = false;
            comment.reactions.like.count = Math.max(
              0,
              comment.reactions.like.count - 1,
            );
          }
          return comment;
        }),
      });

      if (parentCommentId == null) {
        setNews(transformCommentsInNews(type, commentableId, transformFn));
      } else {
        setNews(
          transformRepliesInNews(
            type,
            commentableId,
            parentCommentId,
            transformFn,
          ),
        );
      }
    },
    [],
  );

  const checkRepeatNews = useCallback((news: Array<News>) => {
    const postHash = news
      .filter((n) => n.newsType === 'post')
      .map((n) => n.newsId)
      .reduce(
        (hash, n) => {
          hash[n] = hash[n] ? ++hash[n] : 1;
          return hash;
        },
        {} as Record<string | number, number>,
      );
    Object.keys(postHash)
      .filter((k) => postHash[k] > 1)
      .forEach((k) => {
        arbitraryEvents.write('SocialHomeFeedNewsRepeat', {
          page_number: pageNumberRef.current,
          news_id: k,
          news_type: 'post',
        });
      });
    const paperUpdateHash = news
      .filter((n) => n.newsType === 'paper_update')
      .map((n) => n.newsId)
      .reduce(
        (hash, n) => {
          hash[n] = hash[n] ? ++hash[n] : 1;
          return hash;
        },
        {} as Record<string | number, number>,
      );
    Object.keys(paperUpdateHash)
      .filter((k) => paperUpdateHash[k] > 1)
      .forEach((k) => {
        arbitraryEvents.write('SocialHomeFeedNewsRepeat', {
          page_number: pageNumberRef.current,
          news_id: k,
          news_type: 'paper_update',
        });
      });
  }, []);

  const checkRepeatPage = useCallback((news: Array<News>) => {
    // cast each news item into a string so that we can do string comparison for equality check
    const reducedNews = news.map((n) => [n.newsId, n.newsType].join('_'));
    const lastNews = reducedNews.pop();
    // no need to check if news is empty
    if (!lastNews) return;
    // check if the last news item is seen again in the list of news items
    if (reducedNews.includes(lastNews)) {
      arbitraryEvents.write('SocialHomeFeedPageRepeat', {
        page_number: pageNumberRef.current,
      });
    }
  }, []);

  return {
    news,
    enableInfiniteScroll: !isLoading && hasNextPage.current,
    isLoading,
    hasLoadedNews,
    hasNextPage: hasNextPage.current,
    newsFeedLoadedRef,
    hasError,
    noNewsFound,
    fetchNextPage,
    refreshFeed,
    removeUserContent,
    addFeedNews,
    removeFeedNews,
    updateFeedNews,
    likeFeedNews,
    unlikeFeedNews,
    repostFeedNews,
    unrepostFeedNews,
    updateWorkBookmarkStatus,
    addFeedComment,
    removeFeedComment,
    updateFeedComment,
    likeFeedComment,
    unlikeFeedComment,
    checkRepeatNews,
    checkRepeatPage,
  };
};

export default useNews;
